import React from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

import { LEVER_LABELS } from "../acgSummary";
import {
  CHART_FONT,
  CHART_INK,
  CHART_MUTED,
  CHART_SURFACE,
  categoricalColor,
  legend,
  tooltip,
} from "./chartTheme";

ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * LES FAMILLES DE LEVIERS DES MESURES ADOPTÉES.
 *
 * Un anneau, pas un camembert : le trou porte le total, qui est la première
 * chose que la salle demande (« on a pris combien de mesures, déjà ? »). Les
 * parts sont séparées par un liseré blanc plutôt que par un contour de leur
 * propre couleur — sur un projecteur, deux teintes voisines se recollent.
 *
 * Les libellés viennent de `LEVER_LABELS` (acgSummary.js) au lieu d'une table
 * recopiée ici : le bilan ACG et cet anneau nommaient les mêmes familles à deux
 * endroits, et une famille ajoutée d'un seul côté s'affichait sous son
 * identifiant brut.
 */

/** Le total au centre de l'anneau. Dessiné après coup : chart.js n'a pas de
    notion de contenu central, et une image de fond ne suit pas le total. */
const centerTotal = {
  id: "centerTotal",
  afterDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const values = chart.data.datasets[0]?.data || [];
    const total = values.reduce((sum, v) => sum + (Number(v) || 0), 0);
    if (!total) return;

    const x = (chartArea.left + chartArea.right) / 2;
    const y = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = CHART_INK;
    ctx.font = `800 30px ${CHART_FONT}`;
    ctx.fillText(String(total), x, y - 9);
    ctx.fillStyle = CHART_MUTED;
    ctx.font = `600 12px ${CHART_FONT}`;
    ctx.fillText(total > 1 ? "mesures" : "mesure", x, y + 15);
    ctx.restore();
  },
};

// Même raison que dans RadarChart : la taille en dur débordait des cartes du
// bilan. Les défauts reprennent les valeurs d'origine.
const DecisionCategoryChart = ({ gameStatus, width = "600px", height = "525px" }) => {
  const chosen = gameStatus?.choosen_decisions || {};

  const counts = {};
  Object.values(chosen).forEach((decision) => {
    const type = decision?.type || "inconnu";
    // Les mesures non classées ne font pas une famille : les compter en
    // « Inconnu » donnerait une part à ce qui n'est qu'une fiche incomplète.
    if (type === "inconnu") return;
    counts[type] = (counts[type] || 0) + 1;
  });

  const keys = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);

  if (!keys.length) {
    return (
      <div className="water-footprint-container">
        <div className="water-footprint-title">Types de décisions choisies</div>
        <p className="chart-empty">Aucune mesure classée à afficher.</p>
      </div>
    );
  }

  const data = {
    labels: keys.map((key) => LEVER_LABELS[key] || key),
    datasets: [
      {
        data: keys.map((key) => counts[key]),
        backgroundColor: keys.map((_, i) => categoricalColor(i)),
        borderColor: CHART_SURFACE,
        borderWidth: 3,
        hoverOffset: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: legend({
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8,
          padding: 14,
          color: CHART_MUTED,
          font: { family: CHART_FONT, size: 12, weight: "600" },
        },
      }),
      tooltip: tooltip({
        callbacks: {
          label: (item) => ` ${item.label} : ${item.parsed} mesure${item.parsed > 1 ? "s" : ""}`,
        },
      }),
      datalabels: { display: false },
    },
    layout: { padding: { top: 6 } },
  };

  return (
    <div className="water-footprint-container">
      <div className="water-footprint-title">Types de décisions choisies</div>
      <div style={{ width, height }}>
        <Doughnut data={data} options={options} plugins={[centerTotal]} />
      </div>
    </div>
  );
};

export default DecisionCategoryChart;
